import ArrowDownIcon from "@redotech/redo-icons/arrow-narrow-down_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, type ReactNode, useCallback, useRef, useState } from "react";
import * as z from "zod";
import type { PendingConfirmation } from "../hooks/use-intelligent-inventory";
import * as styles from "./changelog-card.module.css";

const PREVIEW_LIMIT = 10;

const BULK_ACTION_LABELS: Record<string, string> = {
  createProduct: "Create product",
  updateProduct: "Update",
  createSupplier: "Create supplier",
  updateSupplier: "Update supplier",
  createLocation: "Create location",
  setProductLocations: "Update product locations",
  createPurchaseOrder: "Create purchase order",
  updatePurchaseOrder: "Update purchase order",
  createTransfer: "Create transfer",
  submitTransfer: "Submit transfer",
  adjustInventory: "Update inventory levels",
  adjustForecasts: "Update forecasts",
};

const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  sku: "SKU",
  price: "Price",
  cost: "Cost",
  vendor: "Vendor",
  description: "Description",
  weightGrams: "Weight (g)",
  upc: "UPC",
  tags: "Marketplace product tags",
  originCountry: "Origin country",
  hsCode: "HS code",
  supplierIds: "Suppliers",
  imageFileNames: "Images",
  name: "Name",
  street1: "Street 1",
  street2: "Street 2",
  address1: "Address",
  address2: "Address 2",
  city: "City",
  state: "State",
  province: "State / Province",
  postalCode: "Postal code",
  zip: "ZIP code",
  country: "Country",
  countryCode: "Country",
  phone: "Phone",
  company: "Company",
  shippingName: "Shipping label name",
  phoneNumber: "Phone",
  website: "Website",
  currency: "Currency",
  leadTimeDays: "Lead time (days)",
  bufferDays: "Buffer (days)",
  minimumOrderQuantity: "Minimum order quantity",
  reorderThreshold: "Reorder threshold",
  forecastingEnabled: "Forecasting",
  contacts: "Contacts",
  products: "Products",
  status: "Status",
  poNumber: "PO number",
  reference: "Reference",
  trackingUrl: "Tracking URL",
  note: "Note",
  noteToSupplier: "Note to supplier",
  email: "Email",
  supplierId: "Supplier",
  expectedDeliveryDate: "Expected delivery date",
  locationId: "Destination",
  originLocationId: "Origin",
  destinationLocationId: "Destination",
  orderedAt: "Order date",
  expectedQuantity: "Quantity",
  kind: "Type",
  outputQuantity: "Output quantity",
};

interface ProductCurrentValues {
  title?: string | null;
  sku?: string | null;
  price?: number | null;
  cost?: number | null;
  vendor?: string | null;
  description?: string | null;
  upc?: string | null;
  tags?: string[] | null;
  weightGrams?: number | null;
  reorderThreshold?: number | null;
  forecastingEnabled?: boolean | null;
}

interface UpdateProductEntry {
  name: string;
  sku: string | null;
  currentValues: ProductCurrentValues;
  updates: Record<string, unknown>;
}

interface UpdateConfirmationData {
  action: "updateProduct";
  products: UpdateProductEntry[];
}

interface DeleteProductEntry {
  name: string;
  sku: string | null;
}

interface DeleteConfirmationData {
  action: "deleteProduct";
  products: DeleteProductEntry[];
}

interface CreateConfirmationData {
  action: "createProduct";
  product: Record<string, unknown>;
}

interface LinkSuppliersConfirmationData {
  action: "linkSuppliersToProducts";
  suppliers: { id: string; name: string }[];
  products: { id: string; name: string; sku: string | null }[];
}

interface CreateSupplierConfirmationData {
  action: "createSupplier";
  supplier: Record<string, unknown>;
}

interface SupplierCurrentValues {
  name?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  phoneNumber?: string | null;
  website?: string | null;
  currency?: string | null;
  leadTimeDays?: number | null;
}

interface UpdateSupplierEntry {
  name: string;
  currentValues: SupplierCurrentValues;
  updates: Record<string, unknown>;
}

interface UpdateSupplierConfirmationData {
  action: "updateSupplier";
  suppliers: UpdateSupplierEntry[];
}

interface CreateBomConfirmationData {
  action: "createBom";
  name: string;
  output: { sku: string; name: string | null; quantity?: number };
  inputs: BomInputEntry[];
  status?: string;
  tags?: string[] | null;
}

interface BomCurrentValues {
  name?: string | null;
  tags?: string[] | null;
  status?: string | null;
  outputQuantity?: number | null;
}

interface UpdateBomEntry {
  bomId: string;
  name: string;
  currentValues: BomCurrentValues;
  updates: Record<string, unknown>;
}

interface UpdateBomConfirmationData {
  action: "updateBom";
  boms: UpdateBomEntry[];
}

interface BomInputEntry {
  sku: string;
  name: string | null;
  quantity: number;
  position: number;
}

interface VariantEntry {
  sku: string;
  title?: string | null;
  options: Record<string, string>;
  price?: number | null;
  cost?: number | null;
  vendor?: string | null;
  imageFileNames?: string[] | null;
}

const InventoryAdjustmentPreviewSchema = z.object({
  productId: z.string(),
  locationId: z.string(),
  mode: z.string(),
  quantity: z.number(),
  field: z.string().optional(),
});
type InventoryAdjustmentPreview = z.infer<
  typeof InventoryAdjustmentPreviewSchema
>;

type BulkAdjustmentPreview =
  | InventoryAdjustmentPreview
  | ForecastAdjustmentPreview;

/** Display data for an Ask Redo manual-order (fulfillment) write; built by
 *  the merchant app's manual-order-changelog adapter. */
export interface ManualOrderChangelogDisplay {
  orderNumber: string;
  shipFrom: string;
  shipTo: string;
  customer?: string;
  items: { title: string; sku?: string; quantity: number }[];
}

interface BulkChangelogEntry {
  action?: string;
  // createManualOrder fields
  manualOrder?: ManualOrderChangelogDisplay;
  fields?: Record<string, unknown>;
  match?: { sku?: string | null; id?: string | null };
  // BOM-specific fields
  name?: string;
  output?: { sku: string; name: string | null; quantity?: number };
  inputs?: BomInputEntry[];
  status?: string;
  tags?: string[];
  // deleteBom fields
  bomId?: string;
  outputProducts?: { name: string | null; sku: string; quantity: number }[];
  // Product family fields
  title?: string;
  description?: string | null;
  vendor?: string | null;
  imageFileNames?: string[] | null;
  options?: { name: string; values: string[] }[];
  variants?: VariantEntry[];
  familyId?: string;
  familyName?: string;
  // setProductLocations fields
  productIds?: string[];
  locationIds?: string[];
  mode?: "add" | "remove" | "set";
  // adjustInventory fields
  adjustments?: BulkAdjustmentPreview[];
  kind?: string;
  notes?: string | null;
  // linkSuppliersToProducts fields
  supplierIds?: string[];
  links?: {
    supplierId: string;
    productId: string;
    supplierProductTitle?: string | null;
    supplierSku?: string | null;
    unitCost?: number | null;
    currency?: string | null;
  }[];
}

interface AdjustInventoryProductDisplay {
  productTitle: string;
  sku: string | null;
  locations: {
    locationName: string;
    oldQuantity: number;
    newQuantity: number;
  }[];
}

const ForecastAdjustmentPreviewSchema = z.object({
  sku: z.string(),
  date: z.string(),
  delta: z.number(),
});
type ForecastAdjustmentPreview = z.infer<
  typeof ForecastAdjustmentPreviewSchema
>;

function isForecastAdjustment(
  value: unknown,
): value is ForecastAdjustmentPreview {
  return ForecastAdjustmentPreviewSchema.safeParse(value).success;
}

function isInventoryAdjustment(
  value: unknown,
): value is InventoryAdjustmentPreview {
  return InventoryAdjustmentPreviewSchema.safeParse(value).success;
}

function assertNever(value: never): never {
  throw new Error(`Unsupported changelog adjustment: ${JSON.stringify(value)}`);
}

function getForecastAdjustments(
  entry: BulkChangelogEntry,
): ForecastAdjustmentPreview[] {
  const forecastAdjustments: ForecastAdjustmentPreview[] = [];

  for (const adjustment of entry.adjustments ?? []) {
    if (isForecastAdjustment(adjustment)) {
      forecastAdjustments.push(adjustment);
      continue;
    }

    if (isInventoryAdjustment(adjustment)) {
      continue;
    }

    assertNever(adjustment);
  }

  return forecastAdjustments;
}

function formatSignedDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : String(delta);
}

interface BulkConfirmationData {
  changelog: BulkChangelogEntry[];
  summary?: string;
  supplierIdToName?: Record<string, string>;
  deleteProductNames?: Record<string, { title: string; sku: string | null }>;
  productIdToName?: Record<string, string>;
  locationIdToName?: Record<string, string>;
  transferDisplayData?: Record<
    number,
    {
      transferNumber: string;
      originLocationName: string;
      destinationLocationName: string;
      productCount: number;
      totalItems: number;
    }
  >;
  adjustInventoryDisplayData?: Record<number, AdjustInventoryProductDisplay[]>;
}

type ConfirmationData =
  | UpdateConfirmationData
  | CreateConfirmationData
  | DeleteConfirmationData
  | LinkSuppliersConfirmationData
  | CreateSupplierConfirmationData
  | UpdateSupplierConfirmationData
  | CreateBomConfirmationData
  | UpdateBomConfirmationData
  | BulkConfirmationData;

function formatFieldValue(
  key: string,
  value: unknown,
  lookups?: {
    supplierIdToName?: Record<string, string>;
    productIdToName?: Record<string, string>;
    locationIdToName?: Record<string, string>;
  },
): ReactNode {
  if (value == null) {
    return "-";
  }
  if (key === "orderedAt" || key === "expectedDeliveryDate") {
    if (typeof value === "string") {
      const date = new Date(value);
      return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
    }
    return String(value);
  }
  if (key === "price" || key === "cost") {
    return typeof value === "number" ? `$${value.toFixed(2)}` : String(value);
  }
  if (key === "weightGrams") {
    return typeof value === "number" ? `${value}g` : String(value);
  }
  if (key === "reorderThreshold") {
    return typeof value === "number" ? `${value} units` : String(value);
  }
  if (key === "forecastingEnabled") {
    return value === true
      ? "Enabled"
      : value === false
        ? "Disabled"
        : String(value);
  }
  if (key === "tags" && Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "-";
  }
  if (key === "supplierId" && typeof value === "string") {
    return lookups?.supplierIdToName?.[value] ?? value;
  }
  if (
    (key === "locationId" ||
      key === "originLocationId" ||
      key === "destinationLocationId") &&
    typeof value === "string"
  ) {
    return lookups?.locationIdToName?.[value] ?? value;
  }
  if (key === "supplierIds" && Array.isArray(value)) {
    if (value.length === 0) {
      return "-";
    }
    return value
      .map((id) =>
        lookups?.supplierIdToName &&
        typeof id === "string" &&
        lookups.supplierIdToName[id]
          ? lookups.supplierIdToName[id]
          : String(id),
      )
      .join(", ");
  }
  if (key === "contacts" && Array.isArray(value)) {
    if (value.length === 0) {
      return "-";
    }
    return value
      .map((c: Record<string, unknown>) => {
        const parts = [c.name ?? c.email ?? "Unknown"];
        if (c.name && c.email) {
          parts.push(String(c.email));
        }
        if (c.phoneNumber) {
          parts.push(String(c.phoneNumber));
        }
        return parts.length > 1
          ? `${parts[0]} (${parts.slice(1).join(", ")})`
          : String(parts[0]);
      })
      .join("; ");
  }
  if (key === "products" && Array.isArray(value)) {
    if (value.length === 0) {
      return "-";
    }
    return (
      <>
        {value.map((p: Record<string, unknown>, i: number) => {
          const resolvedName =
            typeof p.productId === "string"
              ? lookups?.productIdToName?.[p.productId]
              : undefined;
          const name = p.name ?? p.title ?? resolvedName ?? null;
          const sku = p.sku ? String(p.sku) : null;
          const quantity = p.quantity ?? p.expectedQuantity;
          const qty = quantity != null ? `×${quantity}` : "";
          const label = name
            ? `${name}${sku ? `: ${sku}` : ""} ${qty}`.trim()
            : `${sku ?? "Unknown product"} ${qty}`.trim();
          return <div key={i}>{label}</div>;
        })}
      </>
    );
  }
  if (typeof value === "object" && value !== null) {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map(String).join(", ") : "-";
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v != null,
    );
    return entries.length > 0
      ? entries.map(([k, v]) => `${FIELD_LABELS[k] ?? k}: ${v}`).join(", ")
      : "-";
  }
  return String(value);
}

function isUpdateData(data: ConfirmationData): data is UpdateConfirmationData {
  return "action" in data && data.action === "updateProduct";
}

function isCreateData(data: ConfirmationData): data is CreateConfirmationData {
  return "action" in data && data.action === "createProduct";
}

function isDeleteData(data: ConfirmationData): data is DeleteConfirmationData {
  return "action" in data && data.action === "deleteProduct";
}

function isLinkSuppliersData(
  data: ConfirmationData,
): data is LinkSuppliersConfirmationData {
  return "action" in data && data.action === "linkSuppliersToProducts";
}

function isCreateSupplierData(
  data: ConfirmationData,
): data is CreateSupplierConfirmationData {
  return "action" in data && data.action === "createSupplier";
}

function isUpdateSupplierData(
  data: ConfirmationData,
): data is UpdateSupplierConfirmationData {
  return "action" in data && data.action === "updateSupplier";
}

function isCreateBomData(
  data: ConfirmationData,
): data is CreateBomConfirmationData {
  return "action" in data && data.action === "createBom";
}

function isUpdateBomData(
  data: ConfirmationData,
): data is UpdateBomConfirmationData {
  return "action" in data && data.action === "updateBom";
}

function isBulkData(data: ConfirmationData): data is BulkConfirmationData {
  return "changelog" in data && Array.isArray(data.changelog);
}

function UpdateProductCard({ data }: { data: UpdateConfirmationData }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll
    ? data.products
    : data.products.slice(0, PREVIEW_LIMIT);

  return (
    <>
      {displayed.map((product, i) => {
        const changedFields = Object.entries(product.updates ?? {}).filter(
          ([, v]) => v != null,
        );

        return (
          <div className={styles.productSection} key={i}>
            <Text fontSize="xs" fontWeight="semibold">
              {product.name}
              {product.sku ? ` — ${product.sku}` : ""}
            </Text>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Current</th>
                  <th />
                  <th>New</th>
                </tr>
              </thead>
              <tbody>
                {changedFields.map(([key, newVal]) => {
                  const currentVal =
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    product.currentValues?.[key as keyof ProductCurrentValues];
                  return (
                    <tr key={key}>
                      <td className={styles.fieldName}>
                        {FIELD_LABELS[key] ?? key}
                      </td>
                      <td className={styles.oldValue}>
                        {formatFieldValue(key, currentVal)}
                      </td>
                      <td className={styles.arrow}>{"\u2192"}</td>
                      <td className={styles.newValue}>
                        {formatFieldValue(key, newVal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
      {data.products.length > PREVIEW_LIMIT && (
        <button
          className={styles.showAllButton}
          onClick={() => setShowAll((prev) => !prev)}
          type="button"
        >
          {showAll ? "Show less" : `Show all ${data.products.length} products`}
        </button>
      )}
    </>
  );
}

function CreateProductCard({ data }: { data: CreateConfirmationData }) {
  const fields = Object.entries(data.product).filter(([, v]) => v != null);

  return (
    <div className={styles.productSection}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Field</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(([key, val]) => (
            <tr key={key}>
              <td className={styles.fieldName}>{FIELD_LABELS[key] ?? key}</td>
              <td>{formatFieldValue(key, val)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeleteProductCard({ data }: { data: DeleteConfirmationData }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll
    ? data.products
    : data.products.slice(0, PREVIEW_LIMIT);

  return (
    <>
      {displayed.map((product, i) => (
        <div className={styles.productSection} key={i}>
          <Text fontSize="xs" fontWeight="semibold">
            {product.name}
            {product.sku ? ` — ${product.sku}` : ""}
          </Text>
        </div>
      ))}
      {data.products.length > PREVIEW_LIMIT && (
        <button
          className={styles.showAllButton}
          onClick={() => setShowAll((prev) => !prev)}
          type="button"
        >
          {showAll ? "Show less" : `Show all ${data.products.length} products`}
        </button>
      )}
    </>
  );
}

function LinkSuppliersCard({ data }: { data: LinkSuppliersConfirmationData }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll
    ? data.products
    : data.products.slice(0, PREVIEW_LIMIT);

  return (
    <div className={styles.productSection}>
      {displayed.map((product, i) => (
        <Text fontSize="xs" key={i}>
          {product.name}
          {product.sku ? ` (${product.sku})` : ""}
        </Text>
      ))}
      {data.products.length > PREVIEW_LIMIT && (
        <button
          className={styles.showAllButton}
          onClick={() => setShowAll((prev) => !prev)}
          type="button"
        >
          {showAll ? "Show less" : `Show all ${data.products.length} products`}
        </button>
      )}
    </div>
  );
}

function CreateSupplierCard({
  data,
}: {
  data: CreateSupplierConfirmationData;
}) {
  const fields = Object.entries(data.supplier).filter(
    ([key, v]) => v != null && key !== "contacts",
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const contacts = data.supplier.contacts as
    | { name: string; email?: string | null; phoneNumber?: string | null }[]
    | null
    | undefined;

  return (
    <div className={styles.productSection}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Field</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(([key, val]) => (
            <tr key={key}>
              <td className={styles.fieldName}>{FIELD_LABELS[key] ?? key}</td>
              <td>{formatFieldValue(key, val)}</td>
            </tr>
          ))}
          {contacts && contacts.length > 0 && (
            <tr>
              <td className={styles.fieldName}>Contacts</td>
              <td>
                {contacts.map((c, i) => (
                  <div key={i}>
                    {c.name}
                    {c.email ? ` (${c.email})` : ""}
                    {c.phoneNumber ? ` ${c.phoneNumber}` : ""}
                  </div>
                ))}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function UpdateSupplierCard({
  data,
}: {
  data: UpdateSupplierConfirmationData;
}) {
  return (
    <>
      {data.suppliers.map((supplier, i) => {
        const changedFields = Object.entries(supplier.updates ?? {}).filter(
          ([, v]) => v != null,
        );

        return (
          <div className={styles.productSection} key={i}>
            <Text fontSize="xs" fontWeight="semibold">
              {supplier.name}
            </Text>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Current</th>
                  <th />
                  <th>New</th>
                </tr>
              </thead>
              <tbody>
                {changedFields.map(([key, newVal]) => {
                  const currentVal =
                    supplier.currentValues?.[
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      key as keyof SupplierCurrentValues
                    ];
                  return (
                    <tr key={key}>
                      <td className={styles.fieldName}>
                        {FIELD_LABELS[key] ?? key}
                      </td>
                      <td className={styles.oldValue}>
                        {formatFieldValue(key, currentVal)}
                      </td>
                      <td className={styles.arrow}>{"\u2192"}</td>
                      <td className={styles.newValue}>
                        {formatFieldValue(key, newVal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

function CreateBomCard({ data }: { data: CreateBomConfirmationData }) {
  const sortedInputs = [...data.inputs].sort((a, b) => a.position - b.position);

  return (
    <div className={styles.productSection}>
      <Text fontSize="xs" fontWeight="semibold">
        Create BOM: {data.name}
      </Text>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Role</th>
            <th>SKU</th>
            <th>Name</th>
            <th>Qty</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={styles.fieldName}>Output</td>
            <td>{data.output.sku}</td>
            <td>
              {data.output.name && data.output.name !== data.output.sku
                ? data.output.name
                : "—"}
            </td>
            <td>{data.output.quantity ?? 1}</td>
          </tr>
          {sortedInputs.map((inp, j) => (
            <tr key={j}>
              <td className={styles.fieldName}>Input</td>
              <td>{inp.sku}</td>
              <td>{inp.name && inp.name !== inp.sku ? inp.name : "—"}</td>
              <td>{inp.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UpdateBomCard({ data }: { data: UpdateBomConfirmationData }) {
  return (
    <>
      {data.boms.map((bom, i) => {
        const changedFields = Object.entries(bom.updates ?? {}).filter(
          ([, v]) => v != null,
        );

        return (
          <div className={styles.productSection} key={i}>
            <Text fontSize="xs" fontWeight="semibold">
              {bom.name}
            </Text>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Current</th>
                  <th />
                  <th>New</th>
                </tr>
              </thead>
              <tbody>
                {changedFields.map(([key, newVal]) => {
                  const currentVal =
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    bom.currentValues?.[key as keyof BomCurrentValues];
                  return (
                    <tr key={key}>
                      <td className={styles.fieldName}>
                        {FIELD_LABELS[key] ?? key}
                      </td>
                      <td className={styles.oldValue}>
                        {formatFieldValue(key, currentVal)}
                      </td>
                      <td className={styles.arrow}>{"\u2192"}</td>
                      <td className={styles.newValue}>
                        {formatFieldValue(key, newVal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

function BomEntryCard({
  entry,
  index,
}: {
  entry: BulkChangelogEntry;
  index: number;
}) {
  const title = entry.name ?? entry.output?.sku ?? `Entry ${index + 1}`;
  const sortedInputs = [...(entry.inputs ?? [])].sort(
    (a, b) => a.position - b.position,
  );

  return (
    <div className={styles.productSection}>
      <Text fontSize="xs" fontWeight="semibold">
        Create BOM: {title}
      </Text>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Role</th>
            <th>SKU</th>
            <th>Name</th>
            <th>Qty</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={styles.fieldName}>Output</td>
            <td>{entry.output?.sku ?? "—"}</td>
            <td>
              {entry.output?.name && entry.output.name !== entry.output.sku
                ? entry.output.name
                : "—"}
            </td>
            <td>{entry.output?.quantity ?? 1}</td>
          </tr>
          {sortedInputs.map((inp, j) => (
            <tr key={j}>
              <td className={styles.fieldName}>Input</td>
              <td>{inp.sku}</td>
              <td>{inp.name && inp.name !== inp.sku ? inp.name : "—"}</td>
              <td>{inp.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductFamilyEntryCard({ entry }: { entry: BulkChangelogEntry }) {
  const isAdd = entry.action === "addProductsToFamily";
  const label = isAdd
    ? `Add variants to: ${entry.familyName ?? entry.familyId ?? "Unknown"}`
    : `Create product family: ${entry.title ?? "Unknown"}`;

  const optionNames = (entry.options ?? []).map((o) => o.name);
  const variants = entry.variants ?? [];
  const hasVariantImages = variants.some(
    (v) => v.imageFileNames && v.imageFileNames.length > 0,
  );

  return (
    <div className={styles.productSection}>
      <Text fontSize="xs" fontWeight="semibold">
        {label}
      </Text>
      {!isAdd && entry.options && entry.options.length > 0 && (
        <Text fontSize="xs">
          Options:{" "}
          {entry.options
            .map((o) => `${o.name} (${o.values.join(", ")})`)
            .join(" · ")}
        </Text>
      )}
      {!isAdd && entry.imageFileNames && entry.imageFileNames.length > 0 && (
        <Text fontSize="xs">Images: {entry.imageFileNames.join(", ")}</Text>
      )}
      {variants.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>SKU</th>
              {optionNames.map((name) => (
                <th key={name}>{name}</th>
              ))}
              <th>Price</th>
              <th>Cost</th>
              {hasVariantImages && <th>Images</th>}
            </tr>
          </thead>
          <tbody>
            {variants.map((v, j) => (
              <tr key={j}>
                <td>{v.sku}</td>
                {optionNames.map((name) => (
                  <td key={name}>{v.options[name] ?? "—"}</td>
                ))}
                <td>{v.price != null ? `$${v.price.toFixed(2)}` : "—"}</td>
                <td>{v.cost != null ? `$${v.cost.toFixed(2)}` : "—"}</td>
                {hasVariantImages && (
                  <td>
                    {v.imageFileNames && v.imageFileNames.length > 0
                      ? v.imageFileNames.join(", ")
                      : "—"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BulkChangelogCard({ data }: { data: BulkConfirmationData }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll
    ? data.changelog
    : data.changelog.slice(0, PREVIEW_LIMIT);

  return (
    <>
      {displayed.map((entry, i) => {
        if (entry.action === "createBom") {
          return <BomEntryCard entry={entry} index={i} key={i} />;
        }

        if (
          entry.action === "createProductFamily" ||
          entry.action === "addProductsToFamily"
        ) {
          return <ProductFamilyEntryCard entry={entry} key={i} />;
        }

        if (entry.action === "setProductLocations") {
          const modeVerb =
            entry.mode === "add"
              ? "Add"
              : entry.mode === "remove"
                ? "Remove"
                : "Set";
          const preposition = entry.mode === "remove" ? "from" : "to";
          const productCount = entry.productIds?.length ?? 0;
          const locationNames = (entry.locationIds ?? [])
            .map((id) => data.locationIdToName?.[id] ?? id)
            .join(", ");
          const productNames = (entry.productIds ?? []).map(
            (id) => data.productIdToName?.[id] ?? id,
          );
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                {modeVerb} {productCount}{" "}
                {productCount === 1 ? "product" : "products"} {preposition}{" "}
                {locationNames}
              </Text>
              <Text fontSize="xs">
                {productNames.length <= 5
                  ? productNames.join(", ")
                  : `${productNames.slice(0, 5).join(", ")}, and ${productNames.length - 5} more`}
              </Text>
            </div>
          );
        }

        if (entry.action === "adjustInventory") {
          const products = data.adjustInventoryDisplayData?.[i] ?? [];
          return (
            <>
              {products.map((product, j) => {
                const label = product.sku
                  ? `${product.productTitle} (${product.sku})`
                  : product.productTitle;
                return (
                  <div className={styles.productSection} key={`${i}-${j}`}>
                    <Text fontSize="xs" fontWeight="semibold">
                      Update inventory levels for {label}
                    </Text>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Current</th>
                          <th>New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.locations.map((loc, k) => (
                          <tr key={k}>
                            <td className={styles.fieldName}>
                              {loc.locationName}
                            </td>
                            <td>{loc.oldQuantity}</td>
                            <td>{loc.newQuantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </>
          );
        }

        if (entry.action === "adjustForecasts") {
          const adjustments = getForecastAdjustments(entry);
          const skuCount = new Set(
            adjustments.map((adjustment) => adjustment.sku),
          ).size;
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                Update {adjustments.length} forecast{" "}
                {adjustments.length === 1 ? "adjustment" : "adjustments"} for{" "}
                {skuCount} {skuCount === 1 ? "SKU" : "SKUs"}
              </Text>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Date</th>
                    <th>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map((adjustment, j) => (
                    <tr key={`${adjustment.sku}-${adjustment.date}-${j}`}>
                      <td className={styles.fieldName}>{adjustment.sku}</td>
                      <td>{adjustment.date}</td>
                      <td>{formatSignedDelta(adjustment.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        if (entry.action === "createTransfer") {
          const origin =
            entry.fields?.originLocationId &&
            typeof entry.fields.originLocationId === "string"
              ? (data.locationIdToName?.[entry.fields.originLocationId] ??
                entry.fields.originLocationId)
              : "-";
          const destination =
            entry.fields?.destinationLocationId &&
            typeof entry.fields.destinationLocationId === "string"
              ? (data.locationIdToName?.[entry.fields.destinationLocationId] ??
                entry.fields.destinationLocationId)
              : "-";
          const products = Array.isArray(entry.fields?.products)
            ? entry.fields.products
            : [];
          const totalItems = products.reduce((sum, product) => {
            if (
              product &&
              typeof product === "object" &&
              "expectedQuantity" in product &&
              typeof product.expectedQuantity === "number"
            ) {
              return sum + product.expectedQuantity;
            }
            return sum;
          }, 0);
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                Create transfer from {origin} to {destination}
              </Text>
              <Text fontSize="xs">
                {products.length}{" "}
                {products.length === 1 ? "product" : "products"}
                {totalItems > 0 ? `, ${totalItems} total units` : ""}
              </Text>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, j) => {
                    const productId =
                      product &&
                      typeof product === "object" &&
                      "productId" in product &&
                      typeof product.productId === "string"
                        ? product.productId
                        : "";
                    const quantity =
                      product &&
                      typeof product === "object" &&
                      "expectedQuantity" in product &&
                      typeof product.expectedQuantity === "number"
                        ? product.expectedQuantity
                        : "-";
                    return (
                      <tr key={j}>
                        <td className={styles.fieldName}>
                          {data.productIdToName?.[productId] ?? productId}
                        </td>
                        <td>{quantity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }

        if (entry.action === "createManualOrder" && entry.manualOrder) {
          const order = entry.manualOrder;
          const totalUnits = order.items.reduce(
            (sum, item) => sum + item.quantity,
            0,
          );
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                Create manual order {order.orderNumber}
              </Text>
              <Text fontSize="xs">Ship from: {order.shipFrom}</Text>
              <Text fontSize="xs">Ship to: {order.shipTo}</Text>
              {order.customer && (
                <Text fontSize="xs">Customer: {order.customer}</Text>
              )}
              <Text fontSize="xs">
                {order.items.length}{" "}
                {order.items.length === 1 ? "product" : "products"}
                {totalUnits > 0 ? `, ${totalUnits} total units` : ""}
              </Text>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, j) => (
                    <tr key={j}>
                      <td className={styles.fieldName}>{item.title}</td>
                      <td>{item.sku ?? "—"}</td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        if (entry.action === "submitTransfer") {
          const transfer = data.transferDisplayData?.[i];
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                Submit transfer {transfer?.transferNumber ?? ""}
              </Text>
              {transfer && (
                <Text fontSize="xs">
                  {transfer.originLocationName} to{" "}
                  {transfer.destinationLocationName}; {transfer.productCount}{" "}
                  {transfer.productCount === 1 ? "product" : "products"},{" "}
                  {transfer.totalItems} total units
                </Text>
              )}
            </div>
          );
        }

        if (entry.action === "deleteProduct") {
          const matchKey = entry.match?.id ?? entry.match?.sku ?? null;
          const resolved = matchKey
            ? data.deleteProductNames?.[matchKey]
            : undefined;
          const deleteTitle =
            resolved?.title ?? entry.match?.sku ?? `Entry ${i + 1}`;
          const deleteSku = resolved?.sku ?? entry.match?.sku ?? null;
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                Delete: {deleteTitle}
                {deleteSku && deleteSku !== deleteTitle
                  ? ` — ${deleteSku}`
                  : ""}
              </Text>
            </div>
          );
        }

        if (entry.action === "deleteBom") {
          const outputs = entry.outputProducts ?? [];
          const outputLabel = outputs
            .map((p) =>
              p.name ? `${p.name}${p.sku ? ` (${p.sku})` : ""}` : p.sku,
            )
            .filter(Boolean)
            .join(", ");
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                Delete BOM: {entry.name ?? entry.bomId ?? `Entry ${i + 1}`}
                {entry.status ? ` (${entry.status})` : ""}
              </Text>
              {outputLabel && (
                <Text fontSize="xs">Produces: {outputLabel}</Text>
              )}
            </div>
          );
        }

        if (entry.action === "linkSuppliersToProducts") {
          const links = entry.links ?? [];
          const supplierNames = (entry.supplierIds ?? [])
            .map((id) => data.supplierIdToName?.[id] ?? id)
            .join(", ");
          const productNames = (entry.productIds ?? []).map(
            (id) => data.productIdToName?.[id] ?? id,
          );
          const hasDetails = links.some(
            (l) => l.supplierProductTitle || l.supplierSku || l.unitCost,
          );
          return (
            <div className={styles.productSection} key={i}>
              <Text fontSize="xs" fontWeight="semibold">
                Link {productNames.length}{" "}
                {productNames.length === 1 ? "product" : "products"} to{" "}
                {supplierNames}
              </Text>
              <Text fontSize="xs">
                {productNames.length <= 5
                  ? productNames.join(", ")
                  : `${productNames.slice(0, 5).join(", ")}, and ${productNames.length - 5} more`}
              </Text>
              {hasDetails && (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      {links.some((l) => l.supplierProductTitle) && (
                        <th>Supplier title</th>
                      )}
                      {links.some((l) => l.supplierSku) && (
                        <th>Supplier SKU</th>
                      )}
                      {links.some((l) => l.unitCost != null) && (
                        <th>Unit cost</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((link, j) => {
                      const productName =
                        data.productIdToName?.[link.productId] ??
                        link.productId;
                      return (
                        <tr key={j}>
                          <td className={styles.fieldName}>{productName}</td>
                          {links.some((l) => l.supplierProductTitle) && (
                            <td>{link.supplierProductTitle ?? "—"}</td>
                          )}
                          {links.some((l) => l.supplierSku) && (
                            <td>{link.supplierSku ?? "—"}</td>
                          )}
                          {links.some((l) => l.unitCost != null) && (
                            <td>
                              {link.unitCost != null
                                ? `${link.unitCost}${link.currency ? ` ${link.currency}` : ""}`
                                : "—"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        }

        const fields = Object.entries(entry.fields ?? {}).filter(
          ([, v]) => v != null,
        );
        const resolvedName = entry.match?.id
          ? data.productIdToName?.[entry.match.id]
          : undefined;
        const title =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (entry.fields?.name as string) ??
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (entry.fields?.title as string) ??
          resolvedName ??
          entry.match?.sku ??
          `Entry ${i + 1}`;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const sku = (entry.fields?.sku as string) ?? entry.match?.sku ?? null;

        const actionLabel =
          BULK_ACTION_LABELS[entry.action ?? ""] ??
          (entry.action === "createProduct" ? "Create" : "Update");

        return (
          <div className={styles.productSection} key={i}>
            <Text fontSize="xs" fontWeight="semibold">
              {actionLabel}: {title}
              {sku && sku !== title ? ` (${sku})` : ""}
            </Text>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {fields.map(([key, val]) => (
                  <tr key={key}>
                    <td className={styles.fieldName}>
                      {FIELD_LABELS[key] ?? key}
                    </td>
                    <td>
                      {formatFieldValue(key, val, {
                        supplierIdToName: data.supplierIdToName,
                        productIdToName: data.productIdToName,
                        locationIdToName: data.locationIdToName,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      {data.changelog.length > PREVIEW_LIMIT && (
        <button
          className={styles.showAllButton}
          onClick={() => setShowAll((prev) => !prev)}
          type="button"
        >
          {showAll ? "Show less" : `Show all ${data.changelog.length} entries`}
        </button>
      )}
    </>
  );
}

function getSummaryLabel(data: ConfirmationData | null): string | undefined {
  if (!data) {
    return undefined;
  }
  if (isUpdateData(data)) {
    const count = data.products.length;
    return `Update ${count} ${count === 1 ? "product" : "products"}`;
  }
  if (isCreateData(data)) {
    return "Create 1 product";
  }
  if (isDeleteData(data)) {
    const count = data.products.length;
    return `Delete ${count} ${count === 1 ? "product" : "products"}`;
  }
  if (isLinkSuppliersData(data)) {
    const supplierNames = data.suppliers.map((s) => s.name).join(", ");
    const count = data.products.length;
    return `Link the following ${count} ${count === 1 ? "product" : "products"} to ${supplierNames}?`;
  }
  if (isCreateSupplierData(data)) {
    return "Create 1 supplier";
  }
  if (isUpdateSupplierData(data)) {
    const count = data.suppliers.length;
    return `Update ${count} ${count === 1 ? "supplier" : "suppliers"}`;
  }
  if (isCreateBomData(data)) {
    return `Create bill of materials "${data.name}"`;
  }
  if (isUpdateBomData(data)) {
    const count = data.boms.length;
    return `Update ${count} ${count === 1 ? "bill of materials" : "bills of materials"}`;
  }
  if (isBulkData(data)) {
    const counts: Record<string, number> = {};
    for (const e of data.changelog) {
      const action = e.action ?? "unknown";
      const count =
        action === "adjustForecasts" ? getForecastAdjustments(e).length : 1;
      counts[action] = (counts[action] ?? 0) + count;
    }

    const nounLabels: Record<string, (n: number) => string> = {
      createProduct: (n) => `${n} ${n === 1 ? "product" : "products"}`,
      updateProduct: (n) => `${n} ${n === 1 ? "product" : "products"}`,
      deleteProduct: (n) => `${n} ${n === 1 ? "product" : "products"}`,
      createProductFamily: (n) =>
        `${n} ${n === 1 ? "product family" : "product families"}`,
      addProductsToFamily: (n) =>
        `variants to ${n} ${n === 1 ? "product family" : "product families"}`,
      createBom: (n) => `${n} ${n === 1 ? "BOM" : "BOMs"}`,
      deleteBom: (n) => `${n} ${n === 1 ? "BOM" : "BOMs"}`,
      createSupplier: (n) => `${n} ${n === 1 ? "supplier" : "suppliers"}`,
      updateSupplier: (n) => `${n} ${n === 1 ? "supplier" : "suppliers"}`,
      createLocation: (n) => `${n} ${n === 1 ? "location" : "locations"}`,
      setProductLocations: (n) =>
        `${n} product location ${n === 1 ? "assignment" : "assignments"}`,
      linkSuppliersToProducts: (n) =>
        `${n} supplier-product ${n === 1 ? "link" : "links"}`,
      createPurchaseOrder: (n) =>
        `${n} ${n === 1 ? "purchase order" : "purchase orders"}`,
      updatePurchaseOrder: (n) =>
        `${n} ${n === 1 ? "purchase order" : "purchase orders"}`,
      createTransfer: (n) => `${n} ${n === 1 ? "transfer" : "transfers"}`,
      submitTransfer: (n) => `${n} ${n === 1 ? "transfer" : "transfers"}`,
      createManualOrder: (n) =>
        `${n} ${n === 1 ? "manual order" : "manual orders"}`,
      adjustInventory: (n) =>
        `inventory levels for ${n} ${n === 1 ? "product" : "products"}`,
      adjustForecasts: (n) =>
        `${n} forecast ${n === 1 ? "adjustment" : "adjustments"}`,
    };

    const verbForAction: Record<string, string> = {
      createProduct: "Create",
      updateProduct: "Update",
      deleteProduct: "Delete",
      createProductFamily: "Create",
      addProductsToFamily: "Add",
      createBom: "Create",
      deleteBom: "Delete",
      createSupplier: "Create",
      updateSupplier: "Update",
      createLocation: "Create",
      setProductLocations: "Update",
      linkSuppliersToProducts: "Create",
      createPurchaseOrder: "Create",
      updatePurchaseOrder: "Update",
      createTransfer: "Create",
      submitTransfer: "Submit",
      createManualOrder: "Create",
      adjustInventory: "Update",
      adjustForecasts: "Update",
    };

    // Group actions by verb so we can produce "Create 1 supplier, 4 product families, and 1 purchase order"
    const verbGroups: { verb: string; nouns: string[] }[] = [];
    for (const [action, count] of Object.entries(counts)) {
      const nounFn = nounLabels[action];
      const verb = verbForAction[action];
      if (!nounFn || !verb) {
        continue;
      }
      const existing = verbGroups.find((g) => g.verb === verb);
      if (existing) {
        existing.nouns.push(nounFn(count));
      } else {
        verbGroups.push({ verb, nouns: [nounFn(count)] });
      }
    }

    const parts = verbGroups.map(({ verb, nouns }) => {
      if (nouns.length === 1) {
        return `${verb} ${nouns[0]}`;
      }
      if (nouns.length === 2) {
        return `${verb} ${nouns[0]} and ${nouns[1]}`;
      }
      return `${verb} ${nouns.slice(0, -1).join(", ")}, and ${nouns[nouns.length - 1]}`;
    });
    return parts.join("; ");
  }
  return undefined;
}

export const ChangelogCard = memo(function ChangelogCard({
  confirmation,
  onConfirm,
  onReject,
}: {
  confirmation: PendingConfirmation;
  onConfirm: (toolCallId: string) => void;
  onReject: (toolCallId: string) => void;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const data = confirmation.confirmationData as ConfirmationData | null;
  const summary =
    getSummaryLabel(data) ??
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (data as { summary?: string } | null)?.summary ??
    confirmation.confirmationPrompt;
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const bottomCallbackRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    bottomRef.current = node;
    if (!node) {
      return;
    }
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]) {
          setIsAtBottom(entries[0].isIntersecting);
        }
      },
      { threshold: 0 },
    );
    observerRef.current.observe(node);
  }, []);

  return (
    <div className={styles.cardWrapper}>
      <Flex className={styles.card} dir="column" gap="md">
        <Text fontSize="sm" fontWeight="semibold">
          {summary}
        </Text>
        {data && isUpdateData(data) && <UpdateProductCard data={data} />}
        {data && isCreateData(data) && <CreateProductCard data={data} />}
        {data && isDeleteData(data) && <DeleteProductCard data={data} />}
        {data && isLinkSuppliersData(data) && <LinkSuppliersCard data={data} />}
        {data && isCreateSupplierData(data) && (
          <CreateSupplierCard data={data} />
        )}
        {data && isUpdateSupplierData(data) && (
          <UpdateSupplierCard data={data} />
        )}
        {data && isCreateBomData(data) && <CreateBomCard data={data} />}
        {data && isUpdateBomData(data) && <UpdateBomCard data={data} />}
        {data && isBulkData(data) && <BulkChangelogCard data={data} />}
        <Flex className={styles.actions} gap="sm">
          <RedoButton
            hierarchy="primary"
            onClick={() => onConfirm(confirmation.toolCallId)}
            size="sm"
            text="Confirm"
            theme="action"
          />
          <RedoButton
            hierarchy="secondary"
            onClick={() => onReject(confirmation.toolCallId)}
            size="sm"
            text="Cancel"
            theme="normal"
          />
        </Flex>
        <div ref={bottomCallbackRef} />
      </Flex>
      {!isAtBottom && (
        <div className={styles.scrollToBottomAnchor}>
          <button
            className={styles.scrollToBottom}
            onClick={() =>
              bottomRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            type="button"
          >
            <ArrowDownIcon className={styles.scrollToBottomIcon} />
          </button>
        </div>
      )}
    </div>
  );
});
